import {
  useState,
  useEffect,
  useRef,
  KeyboardEventHandler,
  FocusEvent,
  ChangeEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import AutosizeInput from 'react-input-autosize'
import TextArea from 'react-textarea-autosize'

import { useOnOutsideClick } from '@/hooks'
import { FlexRow, Icon, Text } from '@/ui'
import { ElementWrapper, ToggleButton } from './Styles'
import Editor from '@/components/Field/Editor'

interface InPlaceInputProps {
  inputAs?: any //Can be a string or a third party component of unknown type
  value?: string
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  onKeyPress?: KeyboardEventHandler<HTMLInputElement>
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  showButtons?: boolean
  onSubmit?: (value: string) => void
  onCancel?: () => void
  selectOnFocus?: boolean
  renderExtraButtons?: boolean
}

interface EditableTextProps {
  initialValue?: string
  onSubmit: (value: string) => void
  onBlur?: (value: string) => void
  loading?: boolean
  autoFocusIfNoValue?: boolean
  showButtons?: boolean
  clearable?: boolean
  disabled?: boolean
  autoWidth?: boolean
  selectOnFocus?: boolean
  title?: string
  inputAs?: string
  style?: object
  placeholder?: string
  maxLength?: number
}

const InPlaceInput = ({
  inputAs,
  value = '',
  onChange,
  onKeyPress,
  showButtons,
  onSubmit,
  onCancel,
  selectOnFocus,
  renderExtraButtons,
  ...rest
}: InPlaceInputProps) => {
  const { t } = useTranslation()

  const InputElement = !inputAs || inputAs === 'input' ? AutosizeInput : inputAs

  const useFullWidth = InputElement === TextArea || InputElement === Editor

  return (
    <FlexRow noMargin justifyContent="flex-start" alignItems="flex-end">
      <ElementWrapper
        useFullWidth={useFullWidth}
        allowOverflow={InputElement === Editor}>
        <InputElement
          autoFocus
          {...rest}
          input={{ value }}
          onFocus={(e: FocusEvent<HTMLInputElement, Element>) => {
            if (selectOnFocus) {
              e.target.select()
            }
          }}
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          customFroalaConfig={{
            toolbarButtons: [
              'bold',
              'italic',
              'underline',
              'fontSize',
              'align',
              'formatUL',
              'formatOL',
              'fontFamily',
              'lineHeight',
            ],
          }}
        />
      </ElementWrapper>

      {showButtons && (
        <FlexRow.Section
          style={{
            position: 'relative',
            top: useFullWidth ? '-.75em' : '-2px',
          }}>
          <Icon
            button
            onClick={onCancel}
            title={t('Cancel')}
            name="icon-refresh"
            size="large"
            style={{ transform: 'scaleX(-1)' }}
          />

          <Icon
            button
            onClick={() => onSubmit?.(value)}
            title={t('Save')}
            name="icon-checkmark"
            size="large"
            color="green"
          />

          {renderExtraButtons}
        </FlexRow.Section>
      )}
    </FlexRow>
  )
}

const EditableText = ({
  initialValue = '',
  onSubmit,
  onBlur,
  loading,
  autoFocusIfNoValue = false,
  showButtons = false,
  clearable = false,
  disabled = false,
  autoWidth = false,
  selectOnFocus = false,
  title,
  inputAs = 'input',
  style,
  placeholder: placeholderProp,
  ...rest
}: EditableTextProps) => {
  const { t } = useTranslation()

  const [toggled, setToggled] = useState(!initialValue && autoFocusIfNoValue)

  const [value, setValue] = useState(initialValue)

  const wrapperRef = useRef(null)

  const reset = () => {
    setToggled(false)
    setValue(initialValue)
  }

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useOnOutsideClick(
    wrapperRef,
    async e => {
      // e.path doesn't exist in firefox
      // https://stackoverflow.com/questions/39245488/event-path-is-undefined-running-in-firefox
      const path = e.composedPath && e.composedPath()

      if (
        (path as Element[]).some(({ className }) =>
          className?.includes?.(' popup '),
        )
      ) {
        return
      }

      try {
        if (toggled) {
          if (!onBlur) {
            submit()
            return
          }

          await onBlur(value)

          submit()
        }
      } catch (e) {
        reset()
      }
    },
    {
      ignoreWindowListener: true,
    },
  )

  const submit = async () => {
    setToggled(false)

    const trimmedValue = value?.trim()

    if (trimmedValue === initialValue) {
      return
    }

    if (!clearable && !trimmedValue?.length) {
      setValue(initialValue?.trim())
      return
    }

    try {
      setValue(trimmedValue)
      await onSubmit(trimmedValue)
    } catch (e) {
      reset()
    }
  }

  const placeholder = placeholderProp || t('None')

  if (disabled) {
    return (
      <ToggleButton
        autoWidth={autoWidth}
        isInput={inputAs === 'input'}
        loading={loading}
        disabled={disabled}
        style={style}>
        <Text.Span light>
          {value?.trim?.().length ? value : placeholder}
        </Text.Span>
      </ToggleButton>
    )
  }

  const handleOnCancel = () => {
    setToggled(false)
    setValue(initialValue)
  }

  const trigger =
    // @ts-ignore
    inputAs === Editor ? (
      <span
        title={title}
        dangerouslySetInnerHTML={{
          __html: value?.trim?.().length ? value : placeholder,
        }}
      />
    ) : (
      <span title={title}>{value?.trim?.().length ? value : placeholder}</span>
    )

  const child = toggled ? (
    <InPlaceInput
      {...rest}
      selectOnFocus={selectOnFocus}
      inputAs={inputAs}
      value={value}
      showButtons={showButtons}
      onSubmit={() => {
        setToggled(false)
        submit()
      }}
      onCancel={handleOnCancel}
      onChange={e =>
        // @ts-ignore
        setValue(inputAs === Editor ? (e as unknown as string) : e.target.value)
      }
      onKeyDown={e => {
        if (e.key === 'Escape') {
          handleOnCancel()
          return
        }

        if (e.key === 'Enter' && (!inputAs || inputAs === 'input')) {
          submit()
        }
      }}
    />
  ) : (
    trigger
  )

  return (
    <ToggleButton
      {...rest}
      isInput={inputAs === 'input'}
      loading={loading}
      style={style}
      autoWidth={autoWidth}
      onClick={() => {
        if (toggled) {
          return
        }

        setToggled(true)
      }}>
      <Text.Span light ref={wrapperRef}>
        {child}
      </Text.Span>
    </ToggleButton>
  )
}

export default EditableText
